import { Lock, Shield } from 'lucide-react';

import type { ConductionProposalWire } from '../../../lib/api-client';

export interface TeiaColleague {
  userId: string;
  name: string;
  role: string;
  status: 'online' | 'busy' | 'away' | 'on_call' | 'offline';
  load: string;
  free: boolean;
}

const DOT: Record<TeiaColleague['status'], string> = {
  online: 'st-online',
  on_call: 'st-busy',
  busy: 'st-busy',
  away: 'st-away',
  offline: 'st-away',
};
const AVA: [string, string, string, string] = ['ava--a', 'ava--b', 'ava--c', 'ava--d'];

function avaClass(i: number): string {
  // AVA is a fixed 4-tuple; modulo guarantees a valid index
  return AVA[i % AVA.length]!;
}

function initials(name: string): string {
  return name
    .split(' ')
    .map((w) => w.charAt(0))
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export interface PendingApprovalItem {
  id: string;
  label: string;
  slaMinutes: number | null;
}

export function TeiaRail({
  variant,
  colleagues,
  aiConducting,
  supervisor,
  onAskAuthority,
  onPass,
  pendingApprovals,
  onReviewApprovals,
  proposals,
  onReviewProposal,
}: {
  variant: 'solo' | 'team-pro' | 'regulated';
  colleagues: TeiaColleague[];
  aiConducting: number;
  /** Inc 4 — supervisor primário + presença peer-safe (online/away). null ⇒ esconde. */
  supervisor: { userId: string; name: string; presence: 'online' | 'away' } | null;
  /** Gesto «pedir alçada» (DM ascendente). undefined ⇒ botão oculto (sem a cap). */
  onAskAuthority?: ((supervisorUserId: string) => void) | undefined;
  onPass: (userId: string) => void;
  pendingApprovals: PendingApprovalItem[];
  onReviewApprovals: () => void;
  /** Propostas de condução H_c pendentes (Item 368 F2 Fatia D). */
  proposals: ConductionProposalWire[];
  /** Abre o modal de revisão para a proposta passada. */
  onReviewProposal: (proposal: ConductionProposalWire) => void;
}): JSX.Element {
  const isSolo = variant === 'solo';
  return (
    <aside className="pv3__rail">
      {!isSolo && colleagues.length > 0 && (
        <div className="teia__sec">
          <div className="teia__label">
            <span>Meu time agora</span>
            <span className="peer">peer-safe</span>
          </div>
          <div className="teia__card">
            {colleagues.map((c, i) => (
              <div key={c.userId} className="colleague">
                <span className={`ava ${avaClass(i)}`}>
                  {initials(c.name)}
                  <span className={`ava__dot ${DOT[c.status]}`} />
                </span>
                <span className="colleague__name">
                  {c.name}
                  {c.role !== '' && <small>{c.role}</small>}
                </span>
                {c.free && (
                  <span
                    className="colleague__act"
                    role="button"
                    tabIndex={0}
                    onClick={() => onPass(c.userId)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') onPass(c.userId);
                    }}
                  >
                    passar →
                  </span>
                )}
                <span className="colleague__load">{c.load}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="teia__sec">
        <div className="teia__label">
          <span>A IA, agora</span>
        </div>
        <div className="ai-col">
          <div className="ai-col__head">
            <span className="ai-glyph" />
            <span className="nm">Copiloto Lume</span>
          </div>
          <div className="ai-col__stat">
            {aiConducting > 0 ? (
              <>
                Conduzindo{' '}
                <b>
                  {aiConducting} {aiConducting === 1 ? 'conversa' : 'conversas'}
                </b>{' '}
                de baixo risco.
              </>
            ) : (
              <>Sem conversas sob condução agora.</>
            )}
          </div>
          {/* CTA "Revisar pedido da IA" — Item 368 F2 Fatia D */}
          {proposals.length > 0 && (
            <div className="ai-col__cta" style={{ marginTop: 'var(--space-3)' }}>
              <button
                type="button"
                className="minibtn minibtn--ai"
                style={{ width: '100%' }}
                onClick={() => onReviewProposal(proposals[0]!)}
              >
                Revisar pedido da IA ({proposals.length})
              </button>
            </div>
          )}
        </div>
      </div>

      {isSolo && (
        <div className="teia__sec">
          <div className="teia__label">
            <span>Sozinho no turno</span>
          </div>
          <div className="teia__card">
            <div className="teia__muted">
              Sem time ou supervisor neste plano. A IA é seu apoio — delegue o repetitivo e fique
              com o que precisa de você.
            </div>
          </div>
        </div>
      )}

      {!isSolo && supervisor != null && (
        <div className="teia__sec">
          <div className="teia__label">
            <span>Respaldo</span>
          </div>
          <div className="sup-ch">
            <span className="sup-ch__ic">
              <Shield size={15} />
            </span>
            <span className="sup-ch__txt">
              <b>{supervisor.name}</b>
              <span
                className={`sup-ch__dot ${supervisor.presence === 'online' ? 'st-online' : 'st-away'}`}
              />
              <span className="sup-ch__status">
                {supervisor.presence === 'online' ? 'online' : 'ausente'}
              </span>
              <br />
              Seu respaldo — peça alçada quando precisar.
            </span>
            {onAskAuthority !== undefined && (
              <button
                type="button"
                className="minibtn sup-ch__act"
                onClick={() => onAskAuthority(supervisor.userId)}
              >
                Pedir alçada
              </button>
            )}
          </div>
        </div>
      )}
      {variant === 'regulated' && pendingApprovals.length > 0 && (
        <div className="teia__sec">
          <div className="teia__label">
            <span>Dupla aprovação</span>
          </div>
          <div className="dual">
            <div className="dual__head">
              <Lock size={14} /> Aguardando 2º operador
            </div>
            {pendingApprovals.map((a) => (
              <div key={a.id} className="dual__row">
                <span>{a.label}</span>
                {a.slaMinutes !== null && <span className="dual__sla">SLA {a.slaMinutes} min</span>}
              </div>
            ))}
            <div className="ai-col__cta" style={{ marginTop: 'var(--space-3)' }}>
              <button
                type="button"
                className="minibtn"
                style={{ width: '100%' }}
                onClick={onReviewApprovals}
              >
                Revisar fila
              </button>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}
